// src/RDCalculator.js
import React, { useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FAQSchema from '../../FAQSchema';

// Register necessary components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

const RDCalculator = () => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(50000);
  const [rateOfInterest, setRateOfInterest] = useState(6.5);
  const [timePeriod, setTimePeriod] = useState(3);
  const [investedAmount, setInvestedAmount] = useState(0);
  const [estimatedReturn, setEstimatedReturn] = useState(0);
  const [totalValue, setTotalValue] = useState(0);

  // const calculateRD = () => {
  //   const n = 12; // Monthly compounding
  //   const interestRate = rateOfInterest / 100;
  //   const months = timePeriod * 12;
  //   let investedAmount = monthlyInvestment * months;
  //   let totalValue = 0;

  //   for (let i = 1; i <= months; i++) {
  //     totalValue += monthlyInvestment;
  //     totalValue += totalValue * (interestRate / n);
  //   }

  //   const estimatedReturn = totalValue - investedAmount;

  //   setInvestedAmount(investedAmount);
  //   setEstimatedReturn(estimatedReturn.toFixed(2));
  //   setTotalValue(totalValue.toFixed(2));
  // };
  const calculateRD = () => {
    const months = timePeriod * 12;
    const quarterlyRate = rateOfInterest / (100 * 4);
    let maturity = 0;
  
    for (let i = 0; i < months; i++) {
      const timeRemainingYears = (months - i) / 12;
      maturity += monthlyInvestment * Math.pow(1 + quarterlyRate, 4 * timeRemainingYears);
    }
  
    const investedAmount = monthlyInvestment * months;
    const estimatedReturn = maturity - investedAmount;
  
    setInvestedAmount(investedAmount);
    setEstimatedReturn(estimatedReturn.toFixed(2));
    setTotalValue(maturity.toFixed(2));
  };
  
  const handleMonthlyInvestmentChange = (e) => {
    const value = e.target.value;
    setMonthlyInvestment(value === '' ? 0 : parseInt(value));
  };

  const handleRateOfInterestChange = (e) => {
    const value = e.target.value;
    setRateOfInterest(value === '' ? 0 : parseFloat(value));
  };

  const handleTimePeriodChange = (e) => {
    const value = e.target.value;
    setTimePeriod(value === '' ? 0 : parseInt(value));
  };

  const data = {
    labels: ['Invested Amount', 'Estimated Return'],
    datasets: [
      {
        label: 'RD Breakdown',
        data: [investedAmount, estimatedReturn],
        backgroundColor: ['#36A2EB', '#FF6384'],
      },
    ],
  };
function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
      {
        question: 'What is the minimum amount I can invest in an RD?',
        answer: 'Most banks allow a minimum monthly deposit starting from ₹100, though this may vary slightly between institutions.',
      },
      {
        question: 'How is interest calculated on an RD?',
        answer: 'Interest is usually compounded quarterly and calculated on each monthly installment from the date of deposit until maturity.',
      },
      {
        question: 'Can I open multiple RDs at the same time?',
        answer: 'Yes, you can open multiple RDs across different banks or even within the same bank based on your savings goals.',
      },
      {
        question: 'Is the interest earned on RD taxable?',
        answer: 'Yes, the interest earned is taxable and added to your total income. TDS is also deducted if interest exceeds ₹40,000 (₹50,000 for senior citizens).',
      },
      {
        question: 'Can I withdraw my RD prematurely?',
        answer: 'Yes, but banks usually levy a penalty and adjust interest accordingly for premature withdrawals.',
      },
      {
        question: 'What happens if I miss an RD installment?',
        answer: 'Missing an installment may lead to penalties or reduced interest. Repeated defaults may even result in account closure.',
      },
      {
        question: 'Can I change the RD tenure after opening?',
        answer: 'No, the tenure is fixed at the time of RD initiation and cannot be changed mid-way.',
      },
      {
        question: 'Are RD interest rates fixed or variable?',
        answer: 'They are fixed at the time of booking and remain unchanged during the tenure.',
      },
      {
        question: 'Do all banks offer the same RD interest rates?',
        answer: 'No, interest rates differ across banks and NBFCs. It’s advisable to compare before investing.',
      },
      {
        question: 'Can senior citizens get higher interest rates on RDs?',
        answer: 'Yes, most banks offer additional interest (typically 0.25%–0.75%) for senior citizens.',
      },
      {
        question: 'Is there a nomination facility in RDs?',
        answer: 'Yes, nomination is allowed, enabling the nominee to claim the amount in case of the depositor’s death.',
      },
      {
        question: 'Can I open an RD account online?',
        answer: 'Yes, most banks now offer digital RD account opening via internet banking or mobile apps.',
      },
      {
        question: 'What is the maturity value in RD?',
        answer: 'Maturity value is the total amount received at the end of the RD tenure, including your deposits and earned interest.',
      },
      {
        question: 'Does the RD calculator consider taxes?',
        answer: 'No, most RD calculators provide pre-tax maturity amounts. Taxes are applied separately based on your income slab.',
      },
      {
        question: 'Is the RD calculator result 100% accurate?',
        answer: 'It gives close estimates, but actual maturity may slightly differ depending on bank policies and tax deductions.',
      },
    ];
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
  return (
    <>
     <Helmet>
         <title>RD Calculator – Recurring Deposit Returns & Interest</title>
        <meta  name='description' content='Calculate the interest and maturity value of your recurring deposit investment using our RD calculator.'/>
        <meta name='keywords' content='rd calculator, rd deposit calculator, rd recurring deposit calculator, recurring deposit cal' />
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/rd-calculator" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">RD Calculator</span>
        </li> 
      
      </ol>
    </nav>
      <div className='py-4'>
        <div className="container mx-auto p-4 shadow-xl rounded-md ">
          <h1 className="text-2xl font-bold mb-4">Recurring Deposit Calculator</h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">Monthly Investment</label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    value={monthlyInvestment}
                    onChange={handleMonthlyInvestmentChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">₹ {monthlyInvestment.toLocaleString()}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000"
                  value={monthlyInvestment}
                  onChange={(e) => setMonthlyInvestment(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">Rate of Interest (p.a.)</label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={rateOfInterest}
                    onChange={handleRateOfInterestChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{rateOfInterest} %</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={rateOfInterest}
                  onChange={(e) => setRateOfInterest(parseFloat(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">Time Period (years)</label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={timePeriod}
                    onChange={handleTimePeriodChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{timePeriod} Yr</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={timePeriod}
                  onChange={(e) => setTimePeriod(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <button
                onClick={calculateRD}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Calculate
              </button>
            </div>
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">Results</h2>
                <p>Invested Amount: ₹ {investedAmount.toLocaleString()}</p>
                <p>Estimated Return: ₹ {estimatedReturn.toLocaleString()}</p>
                <p>Total Value: ₹ {totalValue.toLocaleString()}</p>
              </div>
              <div>
                <h2 className="text-xl font-bold">Breakdown</h2>
                <div className="flex justify-center">
                  <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
      <div className="w-full md:w-2/3 p-4 mx-auto">
      <div className="container mx-auto py-4">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            What is a Recurring Deposit (RD)?
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            A Recurring Deposit is a time-bound investment scheme offered by banks and NBFCs that allows individuals to deposit a fixed amount every month and earn interest on it. These deposits generally have a fixed tenure ranging from 6 months to 10 years. The interest is compounded quarterly, and upon maturity, the investor receives the cumulative value of all monthly deposits along with interest earned.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Why Choose Recurring Deposits?
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Regular Saving Habit:</strong> Recurring Deposits instill a disciplined saving approach by mandating fixed monthly contributions. For salaried individuals, this acts as a financial commitment, gradually building a sizable corpus over time.</li>
              <li><strong>Safe and Risk-Free:</strong> Since RDs are not linked to market performance, they are considered low-risk. The principal amount and the promised interest are guaranteed, making it an ideal choice for conservative investors.</li>
              <li><strong>Flexible Tenure:</strong> You can select an RD tenure that suits your goals—whether you're saving for a short-term event like a vacation or long-term goals like education or a down payment on a house.</li>
              <li><strong>Compounded Interest:</strong> Interest is usually compounded quarterly, allowing your money to grow faster compared to simple savings methods.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How is RD Interest Calculated?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The RD maturity amount is not just a straightforward total of all your deposits. It takes into account the interest compounding every quarter. Each monthly deposit earns interest based on the duration it stays in the account until maturity. The formula for RD maturity is:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Maturity Value (A) = P × (1 + R/N)^(Nt)</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>P = Monthly deposit</li>
              <li>R = Annual interest rate (as a decimal)</li>
              <li>N = Compounding frequency (quarterly = 4)</li>
              <li>t = Tenure in years</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              However, doing this manually is cumbersome. This is where an RD Calculator proves useful.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is an RD Calculator?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              An RD Calculator is an online tool that simplifies the entire maturity calculation process. By entering your monthly deposit amount, interest rate, and tenure, the calculator instantly provides you with the maturity value you can expect at the end of your investment term.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How an RD Calculator Works
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Step-by-Step Guide:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Enter Monthly Investment:</strong> Input the fixed amount you intend to deposit every month.</li>
              <li><strong>Select Tenure:</strong> Choose the duration of your RD in months or years.</li>
              <li><strong>Input Interest Rate:</strong> Enter the applicable annual interest rate provided by the bank/NBFC.</li>
              <li><strong>Get Results:</strong> The calculator shows the estimated maturity value, total interest earned, and total amount deposited.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Benefits of Using a Recurring Deposit Calculator
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Effortless Financial Planning:</strong> An RD calculator helps you plan ahead by providing clarity on how much your monthly deposits will grow over time.</li>
              <li><strong>Quick and Accurate Calculations:</strong> Manual calculations are error-prone and complex. The RD calculator eliminates the need for formulas and ensures accuracy.</li>
              <li><strong>Compare Multiple Offers:</strong> You can input interest rates from different banks and compare maturity amounts to decide where to invest.</li>
              <li><strong>Visualize Future Savings:</strong> Seeing your projected returns helps you stay motivated and committed to your savings goals.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Who Should Invest in RDs?
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>First-time investors looking for a safe and consistent savings vehicle.</li>
              <li>Salaried professionals aiming to build an emergency fund.</li>
              <li>Students or parents saving for education or specific future expenses.</li>
              <li>Retired individuals wanting a guaranteed interest income.</li>
              <li>Anyone who prefers low-risk savings with guaranteed returns.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              RD vs FD: Key Differences
            </h1>
            {/* <div className="overflow-x-auto mt-4">
              <table className="table-auto w-full border-collapse border border-gray-300">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="px-4 py-2 text-left">Feature</th>
                    <th className="px-4 py-2 text-left">Recurring Deposit (RD)</th>
                    <th className="px-4 py-2 text-left">Fixed Deposit (FD)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-300 text-gray-600">
                  <tr>
                    <td className="px-4 py-2">Investment Type</td>
                    <td className="px-4 py-2">Monthly Installments</td>
                    <td className="px-4 py-2">One-time Lump Sum</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Ideal For</td>
                    <td className="px-4 py-2">Habitual savers</td>
                    <td className="px-4 py-2">Investors with surplus funds</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Flexibility</td>
                    <td className="px-4 py-2">Medium (fixed monthly amount)</td>
                    <td className="px-4 py-2">High (choose amount & tenure)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Interest Compounding</td>
                    <td className="px-4 py-2">Quarterly</td>
                    <td className="px-4 py-2">Quarterly/Semi-annually</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Premature Withdrawal</td>
                    <td className="px-4 py-2">Possible with penalty</td>
                    <td className="px-4 py-2">Possible with penalty</td>
                  </tr>
                </tbody>
              </table>
            </div> */}
            <div className="max-w-4xl mx-auto overflow-hidden rounded-lg border border-gray-300 shadow mt-6">
    {/* Header */}
    <div className="hidden md:grid grid-cols-3 bg-gray-800 text-white text-sm font-semibold text-center">
      <div className="px-3 py-2 border-r">Feature</div>
      <div className="px-3 py-2 border-r">Recurring Deposit (RD)	</div>
   
      <div className="px-3 py-2">Fixed Deposit (FD)</div>
    </div>

    {/* Data rows for mobile (stacked) and desktop (grid) */}
    <div className="divide-y divide-gray-200 text-sm md:text-base text-gray-600 ">
      {/* Row Template */}
      {[
        {
          feature: "Investment Type",
          rd: "	Monthly Installments",
          fd: "One-time Lump Sum"  
        },
        {
          feature: "Ideal For",
          rd: "Habitual savers",
          fd: "Investors with surplus funds"  
        },
        {
          feature: "Flexibility",
          rd: "Medium (fixed monthly amount)",
          fd: "High (choose amount & tenure)"  
        },
        {
          feature: "Interest Compounding",
          rd: "Quarterly",
          fd: "Quarterly/Semi-annually"  
        },
        {
          feature: "Premature Withdrawal",
          rd: "Possible with penalty",
          fd: "Possible with penalty"  
        },
     
      
      ].map((row, idx) => (
        <div
          key={idx}
          className="block md:grid md:grid-cols-3 hover:bg-gray-50 transition"
        >
          {/* Mobile version */}
          <div className="block md:hidden px-4 py-3">
            <p className="font-semibold">Feature: <span className="font-normal">{row.feature}</span></p>
            <p className="font-semibold">Recurring Deposit (RD): <span className="font-normal">{row.rd}</span></p>
            <p className="font-semibold">Fixed Deposit (FD): <span className="font-normal">{row.fd}</span></p>

          </div>

          {/* Desktop version */}
          <div className="hidden md:block px-4 py-3 border-r">{row.feature}</div>
          <div className="hidden md:block px-4 py-3 border-r">{row.rd}</div>
          <div className="hidden md:block px-4 py-3">{row.fd}</div>

        </div>
      ))}
    </div>
  </div>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Things to Consider Before Starting an RD
            </h1>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Monthly Commitment:</strong> Make sure you can consistently deposit the chosen amount.</li>
              <li><strong>Interest Rates:</strong> Compare between institutions to get the best rates.</li>
              <li><strong>Premature Withdrawal Rules:</strong> Understand the penalties involved if you discontinue early.</li>
              <li><strong>Taxation:</strong> Interest earned on RDs is taxable based on your income slab.</li>
              <li><strong>Tenure Planning:</strong> Match your RD tenure with your financial goals.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Use an RD Calculator for Goal-Based Planning
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Suppose you want to save ₹1 lakh over the next 3 years. Instead of guessing, use the RD calculator to:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Adjust the monthly deposit amount to reach your goal.</li>
              <li>Find the tenure required if you have a fixed monthly budget.</li>
              <li>Compare maturity values at different interest rates offered by banks.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              This helps align your recurring savings with clear, achievable financial targets.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Invest with Confidence
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              RDs are ideal for people who value financial discipline and want to grow their savings gradually without market risk. With the help of an RD calculator, you can estimate future returns, select appropriate tenure, and plan ahead with precision.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Whether you’re saving for a wedding, a gadget, or your child’s school fees, Recurring Deposits offer a structured and secure path toward financial readiness.
            </p>
          </div>

          <h1 className="text-xl font-medium text-gray-700 mt-4">
            “Ready to start saving regularly? Use TheFinMaster to plan your Recurring Deposits and achieve your financial dreams!”
          </h1>
        </div>

        <div className="w-full md:w-1/3 p-4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
              <h2 className="text-lg font-semibold ">Popular Categories</h2>
            </div>
            <div className="p-2 ">
            <ul>
                                <Link to='/financial-tools-calculators/swp-calculator'>
                                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">SWP Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/mutul-fund-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Mutual Fund Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/sukanya-samriddhi-yojana-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Samriddhi Yojana Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/fd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">FD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/rd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">RD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/nps-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NPS Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/hra-exemption-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">HRA Exemption Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/retirement-planning-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Retirement Planning Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">EMI Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/simple-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Simple Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/compound-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Compound Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/nsc-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NSC Calculator</li>

                                </Link><Link to='/financial-tools-calculators/step-up-sip-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Step Up Sip Calculator</li>

                                </Link><Link to='/financial-tools-calculators/income-tax-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Income Tax Calculator</li>
                                    
                                </Link><Link to='/financial-tools-calculators/home-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Home Loan Emi Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/car-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Car Loan Emi Calculator</li>
                                </Link>
                                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                            </ul>
                        </div>
                    </div>
                    <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                        <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                            <h2 className="text-lg font-semibold">More Categories</h2>
                        </div>
                        <div className="p-2">
                        <ul>
                                <Link to='/financial-tools-calculators/gratuity-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/APY-calculator'>
                                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/cagr-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/gst-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/inflation-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/salary-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/brokerage-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/tds-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                </Link>
                {/* {moreCategories.map(category => (
          <Link key={category.id} to={`/category/${category.id}`}>
            <li className="py-2 border-b border-gray-300">{category.name}</li>
          </Link>
        ))} */}
              </ul>
            </div>
          </div>

        </div>

      </div>
      <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
                <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h1>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
    </>
  );
};

export default RDCalculator;
